import { t } from '@/services/i18n';
import { getTableData } from '@/services/api/feed-mapping';
import { handleQueries } from '@/services/queryHandler';
import { addNotification, removeNotification } from '@/stores/notifications.store.js';

export const ALARM_FILTERS_KEY = 'alarm_filters';
export const ALARM_SNOOZE_STATE_KEY = 'alarm_snooze_state';

export const MIN_ALARM_INTERVAL = 15;
export const DEFAULT_ALARM_INTERVAL = 30;
export const MIN_SNOOZE_MINUTES = 1;
export const DEFAULT_SNOOZE_MINUTES = 15;
export const MAX_SNOOZE_MINUTES = 60;

const ALARM_NOTIFICATION_TIMEOUT = 30000;
const INFO_NOTIFICATION_TIMEOUT = 5000;
const MS_PER_MINUTE = 60000;

const NOTIFICATION_IDS = {
  UNMAPPED_EVENTS: 'alarm-unmapped-events',
  API_ERROR: 'alarm-api-error',
  SNOOZE_ENDED: 'alarm-snooze-ended',
  SNOOZE_RESTORED: 'alarm-snooze-restored',
};

const NOTIFICATION_MESSAGES = {
  UNMAPPED_EVENTS: (tenantName) => t('feed_mapping.alarm.unmapped_events', { tenantName }),
  API_ERROR: (tenantName) => t('feed_mapping.alarm.api_error', { tenantName }),
  SNOOZE_ENDED: (tenantName) => t('feed_mapping.alarm.snooze_ended', { tenantName }),
  SNOOZE_RESTORED: (minutes, tenantName) =>
    t('feed_mapping.alarm.snooze_restored', { tenantName, minutes }),
};

const tenantIntervals = new Map();
const tenantSnoozeTimeouts = new Map();
const tenantSnoozeStates = new Map();

function getAllAlarms() {
  try {
    const data = localStorage.getItem(ALARM_FILTERS_KEY);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.error('Failed to parse alarm filters:', e);
    return {};
  }
}

function saveAllAlarms(alarms) {
  try {
    localStorage.setItem(ALARM_FILTERS_KEY, JSON.stringify(alarms));
  } catch (e) {
    console.error('Failed to save alarm filters:', e);
  }
}

export function saveAlarmForTenant(tenantUuid, alarmData) {
  const allAlarms = getAllAlarms();
  allAlarms[tenantUuid] = {
    tenantUuid,
    ...alarmData,
  };
  saveAllAlarms(allAlarms);
}

export function removeAlarmForTenant(tenantUuid) {
  const allAlarms = getAllAlarms();
  delete allAlarms[tenantUuid];
  saveAllAlarms(allAlarms);
}

function getAllSnoozeStates() {
  try {
    const data = localStorage.getItem(ALARM_SNOOZE_STATE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.error('Failed to parse snooze states:', e);
    return {};
  }
}

function saveAllSnoozeStates(states) {
  try {
    localStorage.setItem(ALARM_SNOOZE_STATE_KEY, JSON.stringify(states));
  } catch (e) {
    console.error('Failed to save snooze states:', e);
  }
}

function getDefaultAlarmFilters() {
  return {
    feedType: [],
    feedId: [],
    sportId: [],
    categoryId: [],
    tournamentId: [],
    fromStartDateTime: null,
    toStartDateTime: null,
    mappedStatus: 'ONLY_UNMAPPED',
  };
}

function getAlarmFilters(tenantUuid) {
  const allAlarms = getAllAlarms();
  const tenantAlarm = allAlarms[tenantUuid];

  if (!tenantAlarm) {
    return getDefaultAlarmFilters();
  }

  return tenantAlarm.filters || getDefaultAlarmFilters();
}

function getAlarmInterval(tenantUuid) {
  const filters = getAlarmFilters(tenantUuid);
  return filters?.alarmIntervalSec && filters.alarmIntervalSec >= MIN_ALARM_INTERVAL
    ? filters.alarmIntervalSec
    : DEFAULT_ALARM_INTERVAL;
}

function showNotification(tenantUuid, tenantName) {
  addNotification({
    id: `${NOTIFICATION_IDS.UNMAPPED_EVENTS}-${tenantUuid}`,
    visible: true,
    type: 'warning',
    text: NOTIFICATION_MESSAGES.UNMAPPED_EVENTS(tenantName),
    timeout: ALARM_NOTIFICATION_TIMEOUT,
    actionLabel: t('common.labels.view_events'),
    action: async () => {
      if (window.tenantUuid !== tenantUuid) {
        localStorage.setItem('tenantUuid', tenantUuid);
        window.tenantUuid = tenantUuid;
        window.location.href = '/operations/feed-mapping/events?applyAlarmFilters=1';
      } else {
        handleQueries({
          path: '/operations/feed-mapping/events',
          query: { applyAlarmFilters: 1 },
          keepQuery: false,
        });
      }
    },
  });
}

async function checkForUnmapped(tenantUuid, tenantName) {
  const isSnoozed = tenantSnoozeStates.get(tenantUuid) || false;

  if (isSnoozed) {
    return;
  }

  const filters = getAlarmFilters(tenantUuid);
  const apiFilters = Object.fromEntries(
    Object.entries({
      feedType: filters.feedType,
      feedId: filters.feedId,
      sportId: filters.sportId,
      categoryId: filters.categoryId,
      tournamentId: filters.tournamentId,
      fromStartDateTime: filters.fromStartDateTime,
      toStartDateTime: filters.toStartDateTime,
      mappedStatus: filters.mappedStatus,
    }).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (value === '') return false;
      return true;
    }),
  );

  try {
    const response = await getTableData(apiFilters, tenantUuid);

    if (response?.data?.feedEvents?.length > 0) {
      showNotification(tenantUuid, tenantName);
    }
  } catch (e) {
    addNotification({
      id: `${NOTIFICATION_IDS.API_ERROR}-${tenantUuid}`,
      type: 'error',
      text: NOTIFICATION_MESSAGES.API_ERROR(tenantName),
      timeout: 10000,
    });
    stopAlarmFeedMapping(tenantUuid);
  }
}

export function startAlarmFeedMapping(tenantUuid = null) {
  const targetTenantUuid = tenantUuid || window.tenantUuid;

  if (!targetTenantUuid) {
    console.warn('Cannot start alarm: tenant UUID not available');
    return;
  }

  const allAlarms = getAllAlarms();
  const tenantAlarm = allAlarms[targetTenantUuid];

  if (!tenantAlarm) {
    return;
  }

  if (tenantIntervals.has(targetTenantUuid)) {
    clearInterval(tenantIntervals.get(targetTenantUuid));
    tenantIntervals.delete(targetTenantUuid);
  }

  const tenantName = tenantAlarm.tenantName || 'Unknown';

  const allSnoozeStates = getAllSnoozeStates();
  const snoozeState = allSnoozeStates[targetTenantUuid];

  if (snoozeState) {
    try {
      const { endTime } = snoozeState;
      const remainingTime = endTime - Date.now();

      if (remainingTime > 0) {
        tenantSnoozeStates.set(targetTenantUuid, true);

        const timeoutId = setTimeout(() => {
          tenantSnoozeStates.set(targetTenantUuid, false);
          tenantSnoozeTimeouts.delete(targetTenantUuid);

          const states = getAllSnoozeStates();
          delete states[targetTenantUuid];
          saveAllSnoozeStates(states);

          checkForUnmapped(targetTenantUuid, tenantName);
          addNotification({
            id: `${NOTIFICATION_IDS.SNOOZE_ENDED}-${targetTenantUuid}`,
            type: 'info',
            text: NOTIFICATION_MESSAGES.SNOOZE_ENDED(tenantName),
            timeout: INFO_NOTIFICATION_TIMEOUT,
          });
        }, remainingTime);

        tenantSnoozeTimeouts.set(targetTenantUuid, timeoutId);

        addNotification({
          id: `${NOTIFICATION_IDS.SNOOZE_RESTORED}-${targetTenantUuid}`,
          type: 'info',
          text: NOTIFICATION_MESSAGES.SNOOZE_RESTORED(
            Math.ceil(remainingTime / MS_PER_MINUTE),
            tenantName,
          ),
          timeout: INFO_NOTIFICATION_TIMEOUT,
        });
      } else {
        const states = getAllSnoozeStates();
        delete states[targetTenantUuid];
        saveAllSnoozeStates(states);
        tenantSnoozeStates.set(targetTenantUuid, false);
      }
    } catch (e) {
      console.error('Failed to restore snooze state:', e);
      const states = getAllSnoozeStates();
      delete states[targetTenantUuid];
      saveAllSnoozeStates(states);
      tenantSnoozeStates.set(targetTenantUuid, false);
    }
  } else {
    tenantSnoozeStates.set(targetTenantUuid, false);
  }

  const intervalSec = getAlarmInterval(targetTenantUuid);
  checkForUnmapped(targetTenantUuid, tenantName);

  const intervalId = setInterval(() => {
    checkForUnmapped(targetTenantUuid, tenantName);
  }, intervalSec * 1000);

  tenantIntervals.set(targetTenantUuid, intervalId);
}

export function stopAlarmFeedMapping(tenantUuid = null) {
  const targetTenantUuid = tenantUuid || window.tenantUuid;

  if (!targetTenantUuid) {
    return;
  }

  if (tenantIntervals.has(targetTenantUuid)) {
    clearInterval(tenantIntervals.get(targetTenantUuid));
    tenantIntervals.delete(targetTenantUuid);
  }

  cancelSnooze(targetTenantUuid);
}

export function snoozeAlarm(minutes, tenantUuid = null) {
  const targetTenantUuid = tenantUuid || window.tenantUuid;

  if (!targetTenantUuid) {
    return { success: false, error: 'TENANT_NOT_FOUND' };
  }

  if (!tenantIntervals.has(targetTenantUuid)) {
    return { success: false, error: 'ALARM_NOT_ACTIVE' };
  }

  if (minutes < MIN_SNOOZE_MINUTES) {
    return { success: false, error: 'MIN_VALIDATION' };
  }

  if (minutes > MAX_SNOOZE_MINUTES) {
    return { success: false, error: 'MAX_VALIDATION' };
  }

  cancelSnooze(targetTenantUuid);
  removeNotification(`${NOTIFICATION_IDS.UNMAPPED_EVENTS}-${targetTenantUuid}`);

  tenantSnoozeStates.set(targetTenantUuid, true);
  const snoozeEndTime = Date.now() + minutes * MS_PER_MINUTE;

  try {
    const allStates = getAllSnoozeStates();
    allStates[targetTenantUuid] = {
      endTime: snoozeEndTime,
      minutes,
    };
    saveAllSnoozeStates(allStates);
  } catch (e) {
    console.error('Failed to save snooze state to localStorage:', e);
    tenantSnoozeStates.set(targetTenantUuid, false);
    return { success: false, error: 'STORAGE_ERROR' };
  }

  const allAlarms = getAllAlarms();
  const tenantName = allAlarms[targetTenantUuid]?.tenantName || 'Unknown';

  const milliseconds = minutes * MS_PER_MINUTE;
  const timeoutId = setTimeout(() => {
    tenantSnoozeStates.set(targetTenantUuid, false);
    tenantSnoozeTimeouts.delete(targetTenantUuid);

    const states = getAllSnoozeStates();
    delete states[targetTenantUuid];
    saveAllSnoozeStates(states);

    checkForUnmapped(targetTenantUuid, tenantName);
    addNotification({
      id: `${NOTIFICATION_IDS.SNOOZE_ENDED}-${targetTenantUuid}`,
      type: 'info',
      text: NOTIFICATION_MESSAGES.SNOOZE_ENDED(tenantName),
      timeout: INFO_NOTIFICATION_TIMEOUT,
    });
  }, milliseconds);

  tenantSnoozeTimeouts.set(targetTenantUuid, timeoutId);

  return { success: true };
}

export function cancelSnooze(tenantUuid = null) {
  const targetTenantUuid = tenantUuid || window.tenantUuid;

  if (!targetTenantUuid) {
    return;
  }

  if (tenantSnoozeTimeouts.has(targetTenantUuid)) {
    clearTimeout(tenantSnoozeTimeouts.get(targetTenantUuid));
    tenantSnoozeTimeouts.delete(targetTenantUuid);
  }

  const wasSnoozed = tenantSnoozeStates.get(targetTenantUuid) || false;
  tenantSnoozeStates.set(targetTenantUuid, false);

  const states = getAllSnoozeStates();
  delete states[targetTenantUuid];
  saveAllSnoozeStates(states);

  if (wasSnoozed && tenantIntervals.has(targetTenantUuid)) {
    const allAlarms = getAllAlarms();
    const tenantName = allAlarms[targetTenantUuid]?.tenantName || 'Unknown';
    checkForUnmapped(targetTenantUuid, tenantName);
  }
}

export function isAlarmSnoozed(tenantUuid = null) {
  const targetTenantUuid = tenantUuid || window.tenantUuid;

  if (!targetTenantUuid) {
    return false;
  }

  if (tenantSnoozeStates.get(targetTenantUuid)) {
    return true;
  }

  try {
    const allStates = getAllSnoozeStates();
    const snoozeState = allStates[targetTenantUuid];

    if (!snoozeState) return false;

    const { endTime } = snoozeState;
    const remainingTime = endTime - Date.now();

    if (remainingTime > 0) {
      tenantSnoozeStates.set(targetTenantUuid, true);
      return true;
    } else {
      const states = getAllSnoozeStates();
      delete states[targetTenantUuid];
      saveAllSnoozeStates(states);
      tenantSnoozeStates.set(targetTenantUuid, false);
      return false;
    }
  } catch (e) {
    console.error('Failed to check snooze state:', e);
    const states = getAllSnoozeStates();
    delete states[targetTenantUuid];
    saveAllSnoozeStates(states);
    tenantSnoozeStates.set(targetTenantUuid, false);
    return false;
  }
}

export function isAlarmActive(tenantUuid = null) {
  const targetTenantUuid = tenantUuid || window.tenantUuid;

  if (!targetTenantUuid) {
    return false;
  }

  return tenantIntervals.has(targetTenantUuid);
}

export function getSnoozeRemainingMinutes(tenantUuid = null) {
  const targetTenantUuid = tenantUuid || window.tenantUuid;

  if (!targetTenantUuid) {
    return '';
  }

  try {
    const allStates = getAllSnoozeStates();
    const snoozeState = allStates[targetTenantUuid];

    if (!snoozeState) return '';

    const { endTime } = snoozeState;
    const remainingMs = endTime - Date.now();

    if (remainingMs <= 0) {
      const states = getAllSnoozeStates();
      delete states[targetTenantUuid];
      saveAllSnoozeStates(states);
      return '';
    }

    const minutes = Math.ceil(remainingMs / MS_PER_MINUTE);

    if (minutes < 1) {
      return t('feed_mapping.alarm.less_than_one_min');
    }

    return t('feed_mapping.alarm.minutes_remaining', { minutes });
  } catch (e) {
    console.error('Failed to get snooze remaining time:', e);
    return '';
  }
}

export function startAllActiveAlarms() {
  const allAlarms = getAllAlarms();

  Object.keys(allAlarms).forEach((tenantUuid) => {
    if (tenantUuid && allAlarms[tenantUuid]) {
      startAlarmFeedMapping(tenantUuid);
    }
  });
}

export function getCurrentTenantAlarmFilters() {
  const tenantUuid = window.tenantUuid;
  if (!tenantUuid) return null;

  return getAlarmFilters(tenantUuid);
}
